import { classNames } from '@youshift/shared/utils';
import { useTranslation } from 'react-i18next';
import { RuleOperator } from '@youshift/shared/types';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { Transition } from '@headlessui/react';

import { useChainContext } from '../../../../layouts/ChainLayout';
import LabelIconComponent from '../../../../components/LabelIconComponent';

export type DefineSlotLabelRuleDetailsProps = {
  selectedLabels: number[];
  setSelectedLabels: (labels: number[]) => void;
  selectedRuleOperator: RuleOperator;
  setSelectedRuleOperator: (operator: RuleOperator) => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

export default function DefineSlotLabelRuleDetails({
  selectedLabels,
  setSelectedLabels,
  selectedRuleOperator,
  setSelectedRuleOperator,
  size = 'md',
}: DefineSlotLabelRuleDetailsProps) {
  const { t } = useTranslation();
  const { slotLabels } = useChainContext();

  const handleCheckboxChange = (labelId: number) => {
    if (selectedLabels.includes(labelId)) {
      setSelectedLabels(selectedLabels.filter(id => id !== labelId));
    } else {
      setSelectedLabels([...selectedLabels, labelId]);
    }
  };

  const sizeClasses = {
    sm: 'w-sm',
    md: 'w-md',
    lg: 'w-lg',
    xl: 'w-full',
  };

  return (
    <div className={classNames('space-y-8 px-8', sizeClasses[size])}>
      <div>
        <div className="mt-4">
          {selectedLabels.length >= 2 && (
            <Listbox
              value={selectedRuleOperator}
              onChange={setSelectedRuleOperator}
            >
              {({ open }) => (
                <>
                  <Listbox.Label className="block text-sm font-medium text-gray-700 mb-4">
                    {t('manager.rulesConfig.selectOperator')}
                  </Listbox.Label>
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6">
                      <span className="block truncate">
                        {selectedRuleOperator === RuleOperator.AND
                          ? t('manager.rulesConfig.allLabelsSelected')
                          : t('manager.rulesConfig.anyLabelSelected')}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </span>
                    </Listbox.Button>

                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {[RuleOperator.AND, RuleOperator.OR].map(operator => (
                          <Listbox.Option
                            key={operator}
                            className={({ active }) =>
                              classNames(
                                active
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-900',
                                'relative cursor-default select-none py-2 pl-3 pr-9',
                              )
                            }
                            value={operator}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={classNames(
                                    selected ? 'font-semibold' : 'font-normal',
                                    'block truncate',
                                  )}
                                >
                                  {operator === RuleOperator.AND
                                    ? t('manager.rulesConfig.allLabelsSelected')
                                    : t('manager.rulesConfig.anyLabelSelected')}
                                </span>
                                {selected && (
                                  <span
                                    className={classNames(
                                      active ? 'text-white' : 'text-blue-600',
                                      'absolute inset-y-0 right-0 flex items-center pr-4',
                                    )}
                                  >
                                    ✓
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </>
              )}
            </Listbox>
          )}
        </div>

        <div className="mt-8">
          <label className="block text-sm font-medium text-gray-700">
            {t('manager.rulesConfig.selectLabels')}
          </label>
          <div className="mt-1 space-y-2">
            {Object.values(slotLabels).map(label => (
              <div
                key={label.id_slot_label}
                className={classNames(
                  'relative flex cursor-pointer rounded-lg border p-4',
                  selectedLabels.includes(label.id_slot_label)
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-200',
                )}
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedLabels.includes(label.id_slot_label)}
                      onChange={() => handleCheckboxChange(label.id_slot_label)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      {label.name}
                    </span>
                  </div>
                  <LabelIconComponent icon={label.icon} className="w-5 h-5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

DefineSlotLabelRuleDetails.defaultProps = {
  size: 'md',
};
